import { Component, inject, OnInit, ChangeDetectionStrategy } from '@angular/core'
import { CommonModule } from '@angular/common'
import { FormsModule } from '@angular/forms'
import { TranslatePipe } from '@ngx-translate/core'
import { combineLatest, filter, map } from 'rxjs'
import { ToastService } from '../toast.service'
import { Router } from '@angular/router'
import { UsersService, User } from '../users.service'
import { ToolbarComponent } from '../toolbar/toolbar.component'
import { setSeconds } from 'date-fns'
import { AddCallData, CallsService } from '../calls.service'
import { IntercomService } from '../intercom.service'
import { StepProgressComponent } from '../common/step-progress/step-progress.component'
import { DatetimePickerComponent } from '../common/datetime-picker/datetime-picker.component'
import { SelectionComponent } from '../users-selection/selection.component'
import { MatFormFieldModule } from '@angular/material/form-field'
import { MatInputModule } from '@angular/material/input'
import { MatButtonModule } from '@angular/material/button'
import { MatIconModule } from '@angular/material/icon'
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner'
import { MatIconButtonSizesModule } from 'mat-icon-button-sizes'
import { constructNameForUser, isDateTooFarInPastForManualCallCreation, isTeamsShortMeetingLink } from '../utils'

export const MAXIMUM_CALL_DURATION_IN_MINUTES = 540
export const MINIMUM_CALL_DURATION_IN_MINUTES = 30
export const CALL_DURATION_INCREMENT_IN_MINUTES = 30

@Component({
  selector: 'app-add-call',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslatePipe,
    ToolbarComponent,
    StepProgressComponent,
    DatetimePickerComponent,
    SelectionComponent,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatIconButtonSizesModule,
  ],
  templateUrl: './add-call-page.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './add-call-page.component.scss',
})
export class AddCallPageComponent implements OnInit {
  private toastService = inject(ToastService)
  private router = inject(Router)
  private usersService = inject(UsersService)
  private callsService = inject(CallsService)
  intercomService = inject(IntercomService)

  currentUserQuery$ = this.usersService.getCurrentUser()
  organizationUsers$ = this.usersService.getOrganizationUsers()

  organizationUsersWithoutCurrentUser$ = combineLatest([this.currentUserQuery$, this.organizationUsers$]).pipe(
    map(([currentUserResult, organizationUsersResult]) => {
      if (currentUserResult.isSuccess && organizationUsersResult.isSuccess) {
        const currentUser = currentUserResult.data
        const filteredUsers = organizationUsersResult.data!.filter((user) => user.id !== currentUser!.id)
        return { isSuccess: true, data: filteredUsers }
      } else {
        return { isSuccess: false, data: [] }
      }
    })
  )

  stepLabels = ['MEETING_SETUP.STEPS.MEETING_DETAILS', 'MEETING_SETUP.STEPS.AGENDA_TYPE', 'MEETING_SETUP.STEPS.TEMPLATE_SOURCE', 'MEETING_SETUP.STEPS.REVIEW']

  data: AddCallData = {
    link: '',
    name: '',
    userIds: [],
    date: setSeconds(new Date(), 0),
    duration: 30,
  }
  isCreatingCall: boolean = false

  joinLiveCallMutation = this.callsService.joinLiveCall()

  orgUsesNativeBot$ = this.currentUserQuery$.pipe(map((result) => (result.isSuccess ? (result.data?.organization.useNativeBot ?? false) : false)))

  get hasShortLink(): boolean {
    const link = this.data.link.trim()
    return link !== '' && isTeamsShortMeetingLink(link)
  }

  get isCreateButtonDisabled(): boolean {
    return this.data.name.trim() === '' || this.data.link.trim() === '' || this.isDateTooFarInPast(this.data.date)
  }

  get increaseDurationButtonDisabled(): boolean {
    return this.data.duration >= MAXIMUM_CALL_DURATION_IN_MINUTES
  }

  get decreaseDurationButtonDisabled(): boolean {
    return this.data.duration <= MINIMUM_CALL_DURATION_IN_MINUTES
  }

  isDateTooFarInPast = isDateTooFarInPastForManualCallCreation

  ngOnInit() {
    this.joinLiveCallMutation.result$
      .pipe(
        filter((result) => result.isSuccess),
        map((result) => result.data)
      )
      .subscribe((call) => {
        this.toastService.show('UPLOAD_CALL.IMPORT_SUCCESSFUL_NOTIFICATION')
        this.router.navigate(['/app', 'meetings', call!.id])
      })
  }

  addFullNameToUserList(users: User[]) {
    return users.map((user) => {
      return {
        ...user,
        fullName: constructNameForUser(user),
      }
    })
  }

  onUserSelectionChange(users: User[]) {
    this.data.userIds = users.map((user) => user.id)
  }

  increaseDuration() {
    if (this.data.duration < MAXIMUM_CALL_DURATION_IN_MINUTES) {
      this.data.duration += CALL_DURATION_INCREMENT_IN_MINUTES
    }
  }

  decreaseDuration() {
    if (this.data.duration > MINIMUM_CALL_DURATION_IN_MINUTES) {
      this.data.duration -= CALL_DURATION_INCREMENT_IN_MINUTES
    }
  }

  onCreateCallClicked() {
    this.joinLiveCallMutation.mutate(this.data)
  }
}
